import { useEffect, useState } from 'react';
import { Outlet, useNavigate, useLocation, Link } from 'react-router-dom';
import {
  LayoutDashboard,
  Users,
  Briefcase,
  HelpCircle,
  Image,
  Settings,
  RotateCw,
  Activity,
  FileText,
  LogOut,
  Menu,
  X,
  ClipboardList,
  MessageSquare,
  Music2
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { getMe } from '@/lib/api';

const navItems = [
  { path: '/admin/dashboard', icon: LayoutDashboard, label: 'Дашборд' },
  { path: '/admin/quiz', icon: ClipboardList, label: 'Квиз' },
  { path: '/admin/quiz-responses', icon: MessageSquare, label: 'Ответы квиза' },
  { path: '/admin/managers', icon: Users, label: 'Менеджеры' },
  { path: '/admin/vacancies', icon: Briefcase, label: 'Вакансии' },
  { path: '/admin/faq', icon: HelpCircle, label: 'FAQ' },
  { path: '/admin/gallery', icon: Image, label: 'Галерея' },
  { path: '/admin/rotation', icon: RotateCw, label: 'Ротация' },
  { path: '/admin/settings', icon: Settings, label: 'Настройки' },
  { path: '/admin/pixel', icon: Activity, label: 'Facebook Pixel' },
  { path: '/admin/tiktok-pixel', icon: Music2, label: 'TikTok Pixel' },
  { path: '/admin/logs', icon: FileText, label: 'Логи' },
];

export default function AdminLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const [user, setUser] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  useEffect(() => {
    ensureSession();
  }, []);

  const ensureSession = async () => {
    try {
      const res = await getMe();
      setUser(res.data);
    } catch (err) {
      navigate('/admin/login');
    }
  };

  const doSignOut = () => {
    localStorage.removeItem('token');
    navigate('/admin/login');
  };

  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-100">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-100" data-testid="admin-layout">
      {/* Mobile Header */}
      <div className="lg:hidden fixed top-0 left-0 right-0 z-50 bg-white border-b border-slate-200 px-4 py-3">
        <div className="flex items-center justify-between">
          <h1 className="font-bold text-slate-900">Tempeo</h1>
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="p-2 text-slate-600 hover:text-slate-900"
          >
            {sidebarOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Sidebar */}
      <aside
        className={`
          fixed top-0 left-0 z-40 h-full w-64 bg-white border-r border-slate-200
          transform transition-transform duration-200 ease-in-out
          ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
          lg:translate-x-0
        `}
        data-testid="admin-sidebar"
      >
        <div className="flex flex-col h-full">
          <div className="p-6 border-b border-slate-200 hidden lg:block">
            <h1 className="font-bold text-xl text-slate-900">Tempeo</h1>
            <p className="text-sm text-slate-500">Панель управления</p>
          </div>

          <nav className="flex-1 p-4 mt-14 lg:mt-0 overflow-y-auto">
            <div className="space-y-1">
              {navItems.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={() => setSidebarOpen(false)}
                  className={`
                    admin-nav-item
                    ${location.pathname === item.path ? 'active' : ''}
                  `}
                  data-testid={`nav-${item.path.split('/').pop()}`}
                >
                  <item.icon className="w-5 h-5" />
                  <span>{item.label}</span>
                </Link>
              ))}
            </div>
          </nav>

          <div className="p-4 border-t border-slate-200">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-8 h-8 rounded-full bg-slate-200 flex items-center justify-center">
                <span className="text-sm font-medium text-slate-600">
                  {user.username?.[0]?.toUpperCase() || 'A'}
                </span>
              </div>
              <div>
                <p className="text-sm font-medium text-slate-900">{user.username}</p>
                <p className="text-xs text-slate-500">Администратор</p>
              </div>
            </div>
            <Button
              variant="outline"
              className="w-full"
              onClick={doSignOut}
              data-testid="logout-button"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Выйти
            </Button>
          </div>
        </div>
      </aside>

      {/* Overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-30 bg-black/50 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Main Content */}
      <main className="lg:ml-64 min-h-screen pt-16 lg:pt-0">
        <div className="p-6">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
